import {
  FULFILLMENT_METHODS,
  type FulfillmentMethod,
  ORDER_STATUSES,
  ORDER_TYPES,
  type OrderStatus,
  type OrderType,
} from "@loot-os/fulfillment-core/order/types";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { ErrorAlert } from "../../components/ErrorAlert";
import { centsToDollars, dashIfEmpty, formatDate, formatNumber } from "../../lib/format";
import { useTRPC } from "../../lib/trpc";

const PAGE_SIZE = 50;

/** Select has no unset value, so an explicit member stands for "no filter". */
const ANY = "__any";

const STATUS_LABELS: Record<string, string> = {
  [ANY]: "All statuses",
  ...Object.fromEntries(ORDER_STATUSES.map((status) => [status, status])),
};

const TYPE_LABELS: Record<string, string> = {
  [ANY]: "All types",
  ...Object.fromEntries(ORDER_TYPES.map((type) => [type, type])),
};

const METHOD_LABELS: Record<string, string> = {
  [ANY]: "All methods",
  ...Object.fromEntries(FULFILLMENT_METHODS.map((method) => [method, method])),
};

const COLUMNS = [
  "Order",
  "Tenant",
  "Type",
  "Status",
  "Method",
  "Total",
  "Tracking",
  "Created",
] as const;

/** Fulfillment orders across every tenant: the queue a fulfiller works from. */
export function OrdersPage() {
  const trpc = useTRPC();
  const [status, setStatus] = useState<OrderStatus | typeof ANY>("PENDING");
  const [type, setType] = useState<OrderType | typeof ANY>("MARKETPLACE");
  // MANUAL_BUY is the fulfiller's own queue. It holds a Fanatics withdrawal over the review limit,
  // or placed while the bot is switched off. A BUY_BOT order is a bot's: a Loot OS one exits
  // PENDING once its job exists, and a Boxed mirror one is bought by Boxed's bot. The default
  // shows both.
  const [method, setMethod] = useState<FulfillmentMethod | typeof ANY>(ANY);

  const orders = useInfiniteQuery(
    trpc.platform.fulfillment.orders.search.infiniteQueryOptions(
      {
        limit: PAGE_SIZE,
        statuses: status === ANY ? undefined : [status],
        types: type === ANY ? undefined : [type],
        methods: method === ANY ? undefined : [method],
      },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const rows = orders.data?.pages.flatMap((page) => page.orders) ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Orders</h1>
        <p className="text-sm text-muted-foreground">
          MARKETPLACE orders are bought on the vendor's site: MANUAL_BUY by a fulfiller, BUY_BOT by
          a bot. Loot OS's Fanatics bot buys a BUY_BOT withdrawal; boxed's own bot buys a BUY_BOT
          order mirrored from boxed. Filter to MANUAL_BUY for your own queue, and open one for the
          buy facts.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select
          items={STATUS_LABELS}
          value={status}
          onValueChange={(value) => setStatus(value as OrderStatus)}
        >
          <SelectTrigger size="sm" className="w-44" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          items={TYPE_LABELS}
          value={type}
          onValueChange={(value) => setType(value as OrderType)}
        >
          <SelectTrigger size="sm" className="w-44" aria-label="Type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          items={METHOD_LABELS}
          value={method}
          onValueChange={(value) => setMethod(value as FulfillmentMethod)}
        >
          <SelectTrigger size="sm" className="w-44" aria-label="Method">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(METHOD_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {orders.error && <ErrorAlert message={orders.error.message} />}

      <Card>
        <CardHeader>
          <CardTitle>
            {formatNumber(rows.length)}
            {orders.hasNextPage ? "+" : ""} loaded
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {orders.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No orders match these filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {COLUMNS.map((column) => (
                      <TableHead key={column}>{column}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <Link
                          className="font-mono text-xs underline underline-offset-4"
                          to="/fulfillment/orders/$orderId"
                          params={{ orderId: order.id }}
                        >
                          {order.id}
                        </Link>
                      </TableCell>
                      <TableCell>{order.tenantId}</TableCell>
                      <TableCell>{order.type}</TableCell>
                      <TableCell>{order.status}</TableCell>
                      <TableCell>{dashIfEmpty(order.method)}</TableCell>
                      <TableCell>
                        {order.totalCost === null ? "—" : centsToDollars(order.totalCost)}
                      </TableCell>
                      <TableCell>{dashIfEmpty(order.trackingNumber)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(order.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {orders.hasNextPage && (
            <Button
              type="button"
              variant="outline"
              className="self-start"
              disabled={orders.isFetchingNextPage}
              onClick={() => void orders.fetchNextPage()}
            >
              {orders.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
